import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { browserHistory } from '@/mastodon/components/router';

const STORAGE_KEY = 'paxsudos.mediaFeedNavigation';

const readNavigation = () => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    if (!parsed || parsed.version !== 1 || !Array.isArray(parsed.items)) return null;
    return parsed;
  } catch (_err) {
    return null;
  }
};

const MediaFeedPostNavigation = ({ statusId }) => {
  const [navigation, setNavigation] = useState(() => readNavigation());

  const currentIndex = useMemo(() => {
    if (!navigation?.items?.length) return -1;

    // Do not trust the stored numeric index after history navigation. Resolve
    // from the status actually being displayed.
    return navigation.items.findIndex(
      (item) => String(item.statusId) === String(statusId),
    );
  }, [navigation, statusId]);

  const previous = currentIndex > 0 ? navigation.items[currentIndex - 1] : null;
  const next =
    currentIndex >= 0 && currentIndex < navigation.items.length - 1
      ? navigation.items[currentIndex + 1]
      : null;

  const rememberNavigation = useCallback((item) => {
    if (!item?.path) return;

    const latest = readNavigation();
    if (latest?.items) {
      const newIndex = latest.items.findIndex(
        (candidate) => String(candidate.statusId) === String(item.statusId),
      );
      if (newIndex >= 0) {
        latest.currentIndex = newIndex;
        try {
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(latest));
        } catch (_err) {
          // harmless
        }
        setNavigation(latest);
      }
    }
  }, []);

  const navigate = useCallback((item) => {
    if (!item?.path) return;
    rememberNavigation(item);
    browserHistory.push(item.path);
  }, [rememberNavigation]);

  const handleLinkClick = useCallback((item) => (event) => {
    if (!item?.path) {
      event.preventDefault();
      return;
    }

    // Leave middle-click and modifier clicks to the browser so they open a
    // normal new tab/window. We only maintain the navigation context.
    rememberNavigation(item);
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    browserHistory.push(item.path);
  }, [rememberNavigation]);

  useEffect(() => {
    if (currentIndex < 0) return undefined;

    const handleKeyDown = (event) => {
      // Do not steal arrows while typing, editing controls, or using modifiers.
      const target = event.target;
      if (
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        target?.matches?.('input, textarea, select, [contenteditable="true"]')
      ) return;

      if (event.key === 'ArrowLeft' && previous) {
        event.preventDefault();
        navigate(previous);
      } else if (event.key === 'ArrowRight' && next) {
        event.preventDefault();
        navigate(next);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, navigate, next, previous]);

  if (currentIndex < 0) return null;

  return (
    <nav className='paxsudos-media-feed-post-nav' aria-label='Media-fiidin navigointi'>
      {previous ? (
        <a
          className='paxsudos-media-feed-post-nav__previous'
          href={previous.path}
          onClick={handleLinkClick(previous)}
          title='Edellinen media (←)'
        >
          ← Edellinen
        </a>
      ) : <span className='paxsudos-media-feed-post-nav__edge' />}

      {next ? (
        <a
          className='paxsudos-media-feed-post-nav__next'
          href={next.path}
          onClick={handleLinkClick(next)}
          title='Seuraava media (→)'
        >
          Seuraava →
        </a>
      ) : <span className='paxsudos-media-feed-post-nav__edge' />}
    </nav>
  );
};

export default MediaFeedPostNavigation;
